import { useCallback, useState } from "react";

import { invalidateTags } from "../search/queryCache";
import QUERY_TAGS from "../search/queryTags";
import { getApiErrorFieldCode, getApiErrorStatus } from "../../lib/api/apiError";
import {
  ALL_PAGE_SIZE,
  createRating,
  deleteRating,
  fetchMyRatings,
  updateRating,
} from "./reviewsService";
import { findReviewFor, mapReviews, normalizeTargetType } from "./reviewsMappers";

const ALREADY_EXISTS = "rating.already.exists";

const invalidateReviews = () => {
  invalidateTags([QUERY_TAGS.MY_RATINGS, QUERY_TAGS.SUPPLIER_RATINGS]);
};

const isAlreadyRated = (error) => {
  const status = getApiErrorStatus(error);

  return (
    getApiErrorFieldCode(error) === ALREADY_EXISTS || status === 400 || status === 409
  );
};

const findExistingId = async ({ targetType, targetId }) => {
  const page = await fetchMyRatings({ page: 1, pageSize: ALL_PAGE_SIZE, targetType });
  const rows = mapReviews(page?.data || page?.content || []);

  return findReviewFor(rows, { targetType, targetId })?.id ?? null;
};

export const useSubmitReview = () => {
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(async ({ targetType, targetId, rating, review, existingId }) => {
    const type = normalizeTargetType(targetType);
    const text = String(review || "").trim();

    setSubmitting(true);

    try {
      if (existingId) {
        await updateRating({ id: existingId, rating, review: text });
        invalidateReviews();

        return { status: "updated" };
      }

      try {
        await createRating({ targetType: type, targetId, rating, review: text });
        invalidateReviews();

        return { status: "created" };
      } catch (error) {
        if (!isAlreadyRated(error)) throw error;

        const id = await findExistingId({ targetType: type, targetId });

        if (!id) throw error;

        await updateRating({ id, rating, review: text });
        invalidateReviews();

        return { status: "updated" };
      }
    } catch (error) {
      return { status: "error", error };
    } finally {
      setSubmitting(false);
    }
  }, []);

  return { submit, submitting };
};

export const useDeleteReview = () => {
  const [deleting, setDeleting] = useState(false);

  const remove = useCallback(async (id) => {
    setDeleting(true);

    try {
      await deleteRating(id);
      invalidateReviews();

      return { status: "deleted" };
    } catch (error) {
      return { status: "error", error };
    } finally {
      setDeleting(false);
    }
  }, []);

  return { remove, deleting };
};
